import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    DEFAULT_THEME_MODE,
    getStoredThemeMode,
    getSystemThemePreference,
    normalizeThemeMode,
    persistThemeMode,
    resolveThemeMode,
} from '@/utils/themeMode';

const ThemeModeContext = createContext(null);

const getInitialMode = () => {
    if (typeof window === 'undefined') {
        return DEFAULT_THEME_MODE;
    }

    return getStoredThemeMode(window.localStorage) || DEFAULT_THEME_MODE;
};

const getInitialResolvedMode = () => {
    if (typeof document !== 'undefined') {
        const datasetTheme = document.documentElement.dataset.theme;
        if (datasetTheme === 'light' || datasetTheme === 'dark') {
            return datasetTheme;
        }
    }

    if (typeof window === 'undefined') {
        return 'light';
    }

    return resolveThemeMode(
        getStoredThemeMode(window.localStorage) || DEFAULT_THEME_MODE,
        getSystemThemePreference(window.matchMedia?.bind(window))
    );
};

export function ThemeModeProvider({ children }) {
    const [mode, setModeState] = useState(getInitialMode);
    const [systemPrefersDark, setSystemPrefersDark] = useState(() => (
        typeof window === 'undefined'
            ? false
            : getSystemThemePreference(window.matchMedia?.bind(window))
    ));
    const resolvedMode = useMemo(
        () => resolveThemeMode(mode, systemPrefersDark),
        [mode, systemPrefersDark]
    );
    const [hydratedMode, setHydratedMode] = useState(getInitialResolvedMode);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event) => {
            setSystemPrefersDark(event.matches);
        };

        setSystemPrefersDark(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }

        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleStorage = (event) => {
            if (event.key && event.key !== 'isabhel-theme-mode') {
                return;
            }

            setModeState(getStoredThemeMode(window.localStorage) || DEFAULT_THEME_MODE);
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.documentElement.dataset.theme = resolvedMode;
        document.documentElement.style.colorScheme = resolvedMode;
        setHydratedMode(resolvedMode);
    }, [resolvedMode]);

    const setMode = useCallback((nextMode) => {
        const normalizedMode = normalizeThemeMode(nextMode);
        setModeState(normalizedMode);

        if (typeof window !== 'undefined') {
            persistThemeMode(window.localStorage, normalizedMode);
        }
    }, []);

    const value = useMemo(() => ({
        mode,
        resolvedMode: hydratedMode,
        setMode,
    }), [hydratedMode, mode, setMode]);

    return (
        <ThemeModeContext.Provider value={value}>
            {children}
        </ThemeModeContext.Provider>
    );
}

export function useThemeMode() {
    const context = useContext(ThemeModeContext);

    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider.');
    }

    return context;
}
